import Head from 'next/head';
import { useEffect } from 'react';
import { ComponentProps } from '@components/strapi/DynamicZoneComponent';
import { FaqSectionDTO } from '@lib/strapi/dtos/components/sections/faqSection.dto';
import { Prose, SectionHeading, dataOf } from '../shared-ui';
import { faqAnchor } from '@lib/frontend/search';
import { IconQuestion } from '@components/utils/icons';
import { MDXRemoteSerializeResult } from 'next-mdx-remote';

/**
 * Deep links (#faq-…) must OPEN the accordion, not just scroll past a closed
 * card (owner, 2026-09-10). Runs on mount for direct hits and listens for
 * hashchange for same-page navigations (search overlay → FAQ anchor while
 * already on the page). Renders nothing.
 */
const FaqHashOpener = () => {
  useEffect(() => {
    const openFromHash = () => {
      const id = decodeURIComponent(window.location.hash.replace(/^#/, ''));
      if (!id) return;
      const el = document.getElementById(id);
      if (el instanceof HTMLDetailsElement) {
        el.open = true;
        el.scrollIntoView({ block: 'start' });
      }
    };
    openFromHash();
    window.addEventListener('hashchange', openFromHash);
    return () => window.removeEventListener('hashchange', openFromHash);
  }, []);
  return null;
};

/**
 * FAQ accordion — native <details>, single column at the prose measure
 * (item 11): the old build ran it the full page width, which is hard to scan.
 *
 * FAQPage JSON-LD (item 14) is emitted only where the section says so — true
 * on the FAQ page, false on the homepage, because the same FAQPage markup on
 * two URLs is duplicate structured data. The JSON-LD uses the PLAIN answer
 * text the CMS stores, so the markup never claims something the page does not
 * show.
 */
const FaqSectionImplementation = ({ section, page }: ComponentProps<FaqSectionDTO>) => {
  if (!section) return null;
  const data = dataOf(page);
  type FaqItem = {
    documentId?: string; question?: string; answer?: MDXRemoteSerializeResult; answerText?: string;
  };
  const all = (data.questions ?? []) as FaqItem[];
  // hand-picked questions (relation on the section) beat the source enum —
  // curation draws from ALL questions, in the order the editor arranged them
  const byId = new Map(all.map((q) => [q.documentId, q]));
  const curated = (section.curatedQuestionIds ?? [])
    .map((id) => byId.get(id))
    .filter(Boolean) as FaqItem[];
  const items = curated.length
    ? curated
    : (((section.source === 'HomepageFeatured' ? data.homepageQuestions : data.questions) ?? []) as FaqItem[]);
  if (!items.length) return null;

  return (
    <section className="my-14 md:my-20">
      {section.emitStructuredData ? (
        <Head>
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: items.map((q) => ({
                  '@type': 'Question',
                  name: q.question,
                  acceptedAnswer: { '@type': 'Answer', text: q.answerText ?? '' },
                })),
              }),
            }}
          />
        </Head>
      ) : null}
      <FaqHashOpener />
      <SectionHeading heading={section.heading} intro={section.intro} icon={<IconQuestion />} />
      <div className="measure flex flex-col gap-3">
        {items.map((q, i) => (
          // id + scroll-margin: search results deep-link to individual
          // questions (P-SEARCH); faqAnchor is shared with the index builder
          <details key={i} id={faqAnchor(q.question ?? '')}
            className="group scroll-mt-24 rounded-xl border border-rule bg-white open:border-violet-200 open:bg-violet-050/40">
            {/* text-lg: the 19px serif answers were visually outweighing
                their own 16px questions (owner, 2026-09-09) */}
            <summary className="flex cursor-pointer items-center justify-between gap-4 px-5 py-4 text-lg font-bold text-ink [&::-webkit-details-marker]:hidden">
              {q.question}
              <span aria-hidden="true" className="text-violet-700 transition-transform group-open:rotate-45">+</span>
            </summary>
            <div className="px-5 pb-5">
              {/* answers are multi-paragraph READING text — full prose size
                  (text-sm trapped them at 14px serif; owner, 2026-09-09) */}
              <Prose content={q.answer} />
            </div>
          </details>
        ))}
      </div>
    </section>
  );
};

export default FaqSectionImplementation;
